import React, { useState } from 'react';
import {
    ConveyorDrawingApplyPayload,
    Equipment,
    ExtractedConveyorLayout,
    PredefinedStation,
    ProcessTemplate,
} from '../types';
import {
    conveyorStationsApi,
    drawingApi,
    inventoryApi,
    processTemplatesApi,
} from '../services/api';
import { getIsAdmin } from '../utils/auth';
import DxfMatchingDialog from './DxfMatchingDialog';

interface ConveyorDrawingUploadProps {
    rfqId: string;
    onApplied: () => void;
}

const ConveyorDrawingUpload: React.FC<ConveyorDrawingUploadProps> = ({
    rfqId,
    onApplied,
}) => {
    const isAdmin = getIsAdmin();
    const [file, setFile] = useState<File | null>(null);
    const [parsing, setParsing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [layout, setLayout] = useState<ExtractedConveyorLayout | null>(null);
    const [catalog, setCatalog] = useState<Equipment[]>([]);
    const [stationTypes, setStationTypes] = useState<PredefinedStation[]>([]);
    const [processTemplates, setProcessTemplates] = useState<ProcessTemplate[]>([]);

    if (!isAdmin) return null;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = e.target.files?.[0] ?? null;
        setFile(next);
        setError(null);
    };

    const handleParse = async () => {
        if (!file) return;
        setParsing(true);
        setError(null);
        try {
            const [previewLayout, equipment, types, templates] = await Promise.all([
                drawingApi.preview(rfqId, file),
                inventoryApi.listEquipment(),
                conveyorStationsApi.listStationTypes(),
                processTemplatesApi.list(),
            ]);
            setCatalog(equipment);
            setStationTypes(types);
            setProcessTemplates(templates);
            setLayout(previewLayout);
        } catch (err) {
            const detail =
                (err as { response?: { data?: { detail?: string } } })?.response?.data
                    ?.detail ?? (err instanceof Error ? err.message : 'Failed to parse drawing');
            setError(detail);
        } finally {
            setParsing(false);
        }
    };

    const handleApply = async (payload: ConveyorDrawingApplyPayload) => {
        await drawingApi.apply(rfqId, payload);
        setLayout(null);
        setFile(null);
        onApplied();
    };

    return (
        <div>
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
                <input
                    type="file"
                    accept=".dxf"
                    onChange={handleFileChange}
                    className="block w-full ff-mono text-xs text-[#141414] file:mr-4 file:border file:border-[#141414]/25 file:bg-white file:px-3 file:py-2 file:ff-mono file:text-[10px] file:uppercase file:tracking-wider file:text-[#141414] hover:file:bg-[#141414]/5"
                />
                <button
                    type="button"
                    onClick={handleParse}
                    disabled={!file || parsing}
                    className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 bg-[#141414] text-[#f3efe6] hover:bg-[#e0461e] disabled:opacity-50 transition shrink-0"
                >
                    {parsing ? 'Parsing…' : 'Parse DXF'}
                </button>
            </div>

            {error && (
                <div className="mt-3 ff-mono text-xs text-[#c2251a]">{error}</div>
            )}

            {layout && (
                <DxfMatchingDialog
                    layout={layout}
                    catalog={catalog}
                    processTemplates={processTemplates}
                    stationTypes={stationTypes}
                    onCancel={() => setLayout(null)}
                    onApply={handleApply}
                />
            )}
        </div>
    );
};

export default ConveyorDrawingUpload;
